import React, { useState } from "react"

import { ToastProvider } from "../../contexts/ToastContext"
import { useProcessingVideoReviewsPoll } from "../../hooks/useProcessingVideoReviewsPoll"

import VideoReviewsConfig from "./VideoReviewsConfig"
import VideoReviewsStats from "./VideoReviewsStats"
import VideoReviewsTable from "./VideoReviewsTable"
import VideoReviewsUploadLinks from "./VideoReviewsUploadLinks"

// Helper function to safely get CSRF token for SSR compatibility
const getCSRFToken = () => {
  if (typeof document === "undefined") return ""
  return (
    document.querySelector('[name="csrf-token"]')?.content ||
    document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ||
    ""
  )
}

const VideoReviewsPage = ({ videoReviews, clients }) => {
  const [videoReviewsState, setVideoReviewsState] = useState(videoReviews || [])
  const [loading, setLoading] = useState(false)
  const [selectedClientId, setSelectedClientId] = useState(clients?.[0]?.id || "")

  const { syncProcessingVideo } = useProcessingVideoReviewsPoll(videoReviewsState, setVideoReviewsState)

  const handleApprove = async (videoId) => {
    if (typeof window === "undefined" || !window.confirm("Approve this video?")) return

    setLoading(true)
    try {
      const response = await fetch(`/portal/video_reviews/${videoId}/approve`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCSRFToken()
        }
      })

      if (response.ok) {
        // Actualizar el estado local
        setVideoReviewsState((prevReviews) =>
          prevReviews.map((video) =>
            video.id === videoId ? { ...video, status: "approved", approved_at: new Date().toISOString() } : video
          )
        )
        // Show success message
        if (typeof window !== "undefined") {
          window.location.reload() // Reload to see flash message
        }
      } else {
        if (typeof window !== "undefined") {
          alert("Error approving video")
        }
      }
    } catch (error) {
      console.error("Error:", error) // eslint-disable-line no-console
      if (typeof window !== "undefined") {
        alert("Error approving video")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReject = async (videoId) => {
    if (typeof window === "undefined" || !window.confirm("Reject this video?")) return

    setLoading(true)
    try {
      const response = await fetch(`/portal/video_reviews/${videoId}/reject`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCSRFToken()
        }
      })

      if (response.ok) {
        // Actualizar el estado local
        setVideoReviewsState((prevReviews) =>
          prevReviews.map((video) =>
            video.id === videoId ? { ...video, status: "rejected", approved_at: new Date().toISOString() } : video
          )
        )
        // Show success message
        if (typeof window !== "undefined") {
          window.location.reload() // Reload to see flash message
        }
      } else {
        if (typeof window !== "undefined") {
          alert("Error rejecting video")
        }
      }
    } catch (error) {
      console.error("Error:", error) // eslint-disable-line no-console
      if (typeof window !== "undefined") {
        alert("Error rejecting video")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <ToastProvider>
      <div className="w-full">
        <div className="mb-6 rounded-lg border border-gray-light bg-white p-6 shadow-sm">
          <h5 className="mb-4 text-lg font-semibold text-gray-dark">Create New Upload Link</h5>
          <form action="/portal/video_reviews/create_video_upload_link" method="POST" className="w-full">
            <input type="hidden" name="authenticity_token" value={getCSRFToken()} />
            <div className="flex items-end space-x-4">
              <div className="grow">
                <label htmlFor="client-select" className="mb-1 block text-sm font-medium text-gray">
                  Select a Client
                </label>
                <select
                  id="client-select"
                  name="client_id"
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  className="block w-full rounded-md border-gray-light shadow-sm focus:border-teal focus:ring focus:ring-teal-light focus:ring-opacity-50">
                  {clients && clients.length > 0 ? (
                    clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.first_name} {client.last_name}
                      </option>
                    ))
                  ) : (
                    <option disabled>No clients found</option>
                  )}
                </select>
              </div>
              <button
                type="submit"
                disabled={!selectedClientId || (clients && clients.length === 0)}
                className="inline-flex h-10 items-center justify-center rounded-md border border-transparent bg-teal px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-teal-dark focus:outline-none focus:ring-2 focus:ring-teal-light focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
                Generate Link
              </button>
            </div>
          </form>
        </div>

        <div className="rounded-lg border border-gray-light bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-light p-6">
            <h4 className="flex items-center text-xl font-semibold text-gray-dark">
              <i className="icon-film mr-3 text-2xl text-teal" />
              Video Reviews
            </h4>
          </div>

          <div className="p-6">
            {!videoReviewsState ? (
              <div className="py-12 text-center">
                <i className="icon-film mb-4 block text-6xl text-orange" />
                <h5 className="mb-2 text-lg font-semibold text-orange">Database not configured</h5>
                <p className="text-gray">
                  The video_reviews table doesn&apos;t exist yet. <br />
                  Run <code className="rounded bg-gray-ultralight px-2 py-1 text-sm">rails db:migrate</code> to create
                  the necessary structure.
                </p>
              </div>
            ) : videoReviewsState.length > 0 ? (
              <>
                <VideoReviewsUploadLinks videoReviews={videoReviewsState} />

                <VideoReviewsTable
                  videoReviews={videoReviewsState}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onSyncProcessingStatus={syncProcessingVideo}
                  loading={loading}
                />

                <div className="mt-8">
                  <VideoReviewsStats videoReviews={videoReviewsState} />
                </div>
              </>
            ) : (
              <div className="py-12 text-center">
                <i className="icon-film mb-4 block text-6xl text-gray" />
                <h5 className="mb-2 text-lg font-semibold text-gray-dark">No videos available</h5>
                <p className="mb-6 text-gray">No video reviews have been submitted yet.</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6">
          <VideoReviewsConfig />
        </div>
      </div>
    </ToastProvider>
  )
}

export default VideoReviewsPage
